import { useMemo, useState } from 'react';
import { Alert, Avatar, Button, Card, Chip, Field, Input, ListRow, SectionLabel, Select, Stack } from '../../components';
import { CheckIcon, CopyIcon, RefreshIcon, TrashIcon } from '../../components/icons';
import { CURRENCIES, currencyOptions, parseAmount } from '../../lib/money';
import { addMember, removeMember, renameMember, updateTrip } from '../../data/trips';
import { refreshRates, saveRate } from '../../data/rates';
import { RateFetchError } from '../../data/rates';
import { useSubmit } from '../../hooks/useSubmit';
import { useTrip } from './TripContext';

export function GroupTab() {
  const { trip, members, expenses, rates, myMemberId, isOwner, reload } = useTrip();
  const submit = useSubmit();

  const [newName, setNewName] = useState('');
  const [tripName, setTripName] = useState(trip.name);
  const [copied, setCopied] = useState(false);
  const [rateNotice, setRateNotice] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const hasExpenses = expenses.length > 0;

  const currenciesInUse = useMemo(() => {
    const codes = new Set<string>([trip.local_currency ?? '', ...expenses.map((e) => e.currency)]);
    codes.delete('');
    codes.delete(trip.base_currency);
    // Everything the trip touches first, then the rest of the picker, so the
    // rates that matter are at the top of the list rather than alphabetical.
    const rest = CURRENCIES.map((c) => c.code).filter(
      (code) => code !== trip.base_currency && !codes.has(code),
    );
    return [...codes, ...rest];
  }, [trip.local_currency, trip.base_currency, expenses]);

  async function saveTripName() {
    const trimmed = tripName.trim();
    if (!trimmed || trimmed === trip.name) return;
    if (await submit.run(() => updateTrip(trip.id, { name: trimmed }))) reload();
  }

  async function changeCurrency(field: 'base_currency' | 'local_currency', value: string) {
    if (await submit.run(() => updateTrip(trip.id, { [field]: value }))) reload();
  }

  async function handleAddMember() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (await submit.run(() => addMember(trip.id, trimmed))) {
      setNewName('');
      reload();
    }
  }

  async function handleRename(memberId: string, name: string) {
    if (await submit.run(() => renameMember(memberId, name))) reload();
  }

  async function handleRemove(memberId: string, name: string) {
    if (!window.confirm(`Remove ${name} from this trip?`)) return;
    if (await submit.run(() => removeMember(memberId))) reload();
  }

  async function handleRefreshRates() {
    setRefreshing(true);
    setRateNotice(null);
    try {
      const count = await refreshRates(trip.base_currency, currenciesInUse);
      setRateNotice(`Updated ${count} rate${count === 1 ? '' : 's'}.`);
      reload();
    } catch (error) {
      setRateNotice(
        error instanceof RateFetchError
          ? `${error.message} Type the rate in below instead — it works exactly the same.`
          : 'Could not fetch rates. Type them in below instead.',
      );
    } finally {
      setRefreshing(false);
    }
  }

  async function handleManualRate(quote: string, text: string) {
    const value = parseAmount(text);
    if (value == null || value <= 0) return;
    if (await submit.run(() => saveRate(trip.base_currency, quote, value, 'client'))) reload();
  }

  async function copyInvite() {
    try {
      await navigator.clipboard.writeText(trip.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is blocked outside a secure context; the code is on screen
      // anyway, which is the fallback.
      setCopied(false);
    }
  }

  return (
    <Stack gap={5}>
      <h1 style={{ fontSize: 'var(--fs-display)', fontWeight: 300, letterSpacing: '-0.02em' }}>
        Group
      </h1>

      {submit.error && <Alert tone="error">{submit.error}</Alert>}

      {/* invite */}
      <div>
        <SectionLabel>Invite code</SectionLabel>
        <Card>
          <Stack gap={3}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
              <span
                className="num"
                style={{ fontSize: 30, letterSpacing: '0.16em', flex: 1, minWidth: 0 }}
              >
                {trip.invite_code}
              </span>
              <Button variant="secondary" size="sm" onClick={() => void copyInvite()}>
                {copied ? <CheckIcon size={15} /> : <CopyIcon size={15} />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
              Anyone with this code can join the trip and claim a seat. Send it to the group.
            </p>
          </Stack>
        </Card>
      </div>

      {/* trip settings */}
      <div>
        <SectionLabel>Trip</SectionLabel>
        <Card>
          <Stack gap={4}>
            <Field label="Name" hint={isOwner ? undefined : 'Only the trip owner can change this.'}>
              {({ id }) => (
                <Input
                  id={id}
                  value={tripName}
                  disabled={!isOwner}
                  onChange={(e) => setTripName(e.target.value)}
                  onBlur={() => void saveTripName()}
                />
              )}
            </Field>

            <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
              <div style={{ flex: 1 }}>
                <Field
                  label="Home currency"
                  hint={hasExpenses ? 'Locked once expenses exist.' : 'What balances are shown in.'}
                >
                  {({ id }) => (
                    <Select
                      id={id}
                      value={trip.base_currency}
                      disabled={!isOwner || hasExpenses}
                      onChange={(e) => void changeCurrency('base_currency', e.target.value)}
                    >
                      {currencyOptions(trip.base_currency).map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
              <div style={{ flex: 1 }}>
                <Field label="Spending in" hint="The default for new expenses.">
                  {({ id }) => (
                    <Select
                      id={id}
                      value={trip.local_currency ?? trip.base_currency}
                      disabled={!isOwner}
                      onChange={(e) => void changeCurrency('local_currency', e.target.value)}
                    >
                      {currencyOptions(trip.base_currency, trip.local_currency).map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
            </div>
          </Stack>
        </Card>
      </div>

      {/* people */}
      <div>
        <SectionLabel>People</SectionLabel>
        <Card flush>
          {members.map((member, index) => (
            <MemberRow
              key={`${member.id}:${member.display_name}`}
              divider={index > 0}
              memberId={member.id}
              name={member.display_name}
              isYou={member.id === myMemberId}
              isOwner={member.role === 'owner'}
              unclaimed={member.user_id == null}
              canRename={isOwner || member.id === myMemberId || member.user_id == null}
              canRemove={isOwner && member.role !== 'owner'}
              busy={submit.submitting}
              onRename={(name) => void handleRename(member.id, name)}
              onRemove={() => void handleRemove(member.id, member.display_name)}
            />
          ))}
        </Card>

        {/* Non-negotiable #3: a seat is not an account. Anyone can add one. */}
        <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
          <Input
            value={newName}
            placeholder="Add someone by name"
            aria-label="Add someone by name"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAddMember();
            }}
          />
          <Button
            variant="secondary"
            onClick={() => void handleAddMember()}
            disabled={!newName.trim() || submit.submitting}
          >
            Add
          </Button>
        </div>
        <p style={{ marginTop: 'var(--sp-2)', color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
          They do not need an account yet. When they sign up and enter the invite code, they can
          claim their seat and keep everything already logged against their name.
        </p>
      </div>

      {/* rates */}
      <div>
        <SectionLabel
          right={
            <Button variant="ghost" size="sm" onClick={() => void handleRefreshRates()} loading={refreshing}>
              {!refreshing && <RefreshIcon size={15} />}
              Refresh
            </Button>
          }
        >
          Exchange rates
        </SectionLabel>

        {rateNotice && (
          <div style={{ marginBottom: 'var(--sp-3)' }}>
            <Alert tone={rateNotice.startsWith('Updated') ? 'success' : 'warning'}>{rateNotice}</Alert>
          </div>
        )}

        <Card flush>
          {currenciesInUse.map((code, index) => (
            <RateRow
              key={`${code}:${rates.get(code)?.rate ?? 'none'}`}
              divider={index > 0}
              quote={code}
              base={trip.base_currency}
              known={rates.get(code) ?? null}
              busy={submit.submitting}
              onSave={(text) => void handleManualRate(code, text)}
            />
          ))}
        </Card>

        <p style={{ marginTop: 'var(--sp-3)', color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
          A rate you type here is shared with everyone on the trip, and is used as the default for
          new expenses. Rates already recorded against an expense never change.
        </p>
      </div>
    </Stack>
  );
}

function MemberRow({
  divider,
  memberId,
  name,
  isYou,
  isOwner,
  unclaimed,
  canRename,
  canRemove,
  busy,
  onRename,
  onRemove,
}: {
  divider: boolean;
  memberId: string;
  name: string;
  isYou: boolean;
  isOwner: boolean;
  unclaimed: boolean;
  canRename: boolean;
  canRemove: boolean;
  busy: boolean;
  onRename: (name: string) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState(name);

  return (
    <ListRow divider={divider}>
      <Avatar memberId={memberId} displayName={name} size={38} muted={unclaimed} />

      <input
        value={draft}
        disabled={!canRename || busy}
        aria-label={`Name for ${name}`}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const trimmed = draft.trim();
          if (trimmed && trimmed !== name) onRename(trimmed);
          else setDraft(name);
        }}
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontSize: 'var(--fs-body-lg)',
          color: 'var(--text)',
        }}
      />

      {isYou && <Chip readOnly>you</Chip>}
      {isOwner && !isYou && <Chip readOnly>owner</Chip>}
      {unclaimed && !isYou && (
        <Chip readOnly title="Added by name — they have not signed in yet">
          unclaimed
        </Chip>
      )}

      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          aria-label={`Remove ${name}`}
          style={{
            background: 'none',
            border: 'none',
            padding: 4,
            cursor: 'pointer',
            color: 'var(--text-muted)',
            flex: '0 0 auto',
          }}
        >
          <TrashIcon size={17} />
        </button>
      )}
    </ListRow>
  );
}

function RateRow({
  divider,
  quote,
  base,
  known,
  busy,
  onSave,
}: {
  divider: boolean;
  quote: string;
  base: string;
  known: { rate: number; asOf: string; source: string } | null;
  busy: boolean;
  onSave: (text: string) => void;
}) {
  const [draft, setDraft] = useState(known ? String(known.rate) : '');

  return (
    <ListRow divider={divider}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--fs-body-lg)' }}>
          1 {quote} <span style={{ color: 'var(--text-muted)' }}>in {base}</span>
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>
          {known
            ? `${known.source === 'client' ? 'Set by hand' : 'Fetched'}${known.asOf ? ` · ${known.asOf}` : ''}`
            : 'Not set'}
        </div>
      </div>
      <input
        inputMode="decimal"
        value={draft}
        disabled={busy}
        placeholder="—"
        aria-label={`Rate for 1 ${quote} in ${base}`}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft.trim() && draft !== String(known?.rate ?? '')) onSave(draft);
        }}
        className="num"
        style={{
          width: 108,
          textAlign: 'right',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontSize: 'var(--fs-body-lg)',
          color: 'var(--text)',
        }}
      />
    </ListRow>
  );
}
